<template>
    <div class="email-broadcast-list">
        <div class="header-container">
            <h2 class="broadcast-title">{{ $t("message.emailBroadcasts") }}</h2>
            <div class="broadcast-limit">
                {{
                    monthlyDetail
                        ? `${$t("message.monthlyLimit")}: ${site.monthlyMailSendLimit || 5000} / ${monthlyDetail.count} ${$t("mail.sent")}`
                        : ""
                }}
            </div>
        </div>
        <div v-if="!isCreatingBroadcast">
            <div class="toolbar-container">
                <div class="left-section">
                    <div class="view-switcher">
                        <div class="supsis-button-group">
                            <SupsisButton @click="setMode('active')" :primary="mode === 'active'" small>
                                {{ $t("message.active") }}
                            </SupsisButton>
                            <SupsisButton @click="setMode('archived')" :primary="mode === 'archived'" small>
                                {{ $t("message.archived") }}
                            </SupsisButton>
                        </div>
                    </div>
                </div>
                <div class="right-section">
                    <SupsisButton @click="toggleCreateBroadcast" primary>
                        <div class="material-icons">add</div>
                        {{ $t("message.createBroadcast") }}
                    </SupsisButton>
                </div>
            </div>

            <supsis-table
                :columns="columns"
                :rows="emailBroadcasts"
                :pagination-cursor="paginationCursor"
                @cursor-changed="paginationChanged"
                :top-pagination="true"
                :bottom-pagination="true"
            />
        </div>
        <EmailBroadcast @broadcast-created="onBroadcastCreated" @close="isCreatingBroadcast = false" v-else />

        <a-modal
            v-if="broadcastDetail"
            :visible="!!broadcastDetail"
            :title="$t('message.broadcastDetails')"
            @cancel="closeBroadcastDetail"
            width="80%"
        >
            <a-alert
                v-if="!detailedBroadcastReport"
                :message="$t('message.youCanCheckYourEmailStatus')"
                type="warning"
                show-icon
                banner
            />
            <div class="broadcast-detail">
                <div class="broadcast-detail-item" :status="showingBroadcast.status">
                    <div class="broadcast-detail-item-title">{{ $t("message.status") }}</div>
                    <div class="broadcast-detail-item-value">{{ getStatus(showingBroadcast.status) }}</div>
                </div>
                <div class="broadcast-detail-item" :completed="showingBroadcast.status === 'completed'">
                    <div class="broadcast-detail-item-title">{{ $t("message.sentDetails") }}</div>
                    <div class="broadcast-detail-item-value">
                        {{ showingBroadcast.lastSentIndex }} / {{ showingBroadcast.total }}
                    </div>
                </div>
                <div class="broadcast-detail-item success">
                    <div class="broadcast-detail-item-title">{{ $t("task.success") }}</div>
                    <div class="broadcast-detail-item-value">{{ showingBroadcast.successMessageCount }}</div>
                </div>
                <div class="broadcast-detail-item failed">
                    <div class="broadcast-detail-item-title">{{$t("message.failed")}}</div>
                    <div class="broadcast-detail-item-value">{{ showingBroadcast.failedMessageCount }}</div>
                </div>
            </div>
            <div class="broadcast-status">
                <div class="broadcast-status-item">
                    <div class="broadcast-status-item-icon" type="total">
                        <div class="material-icons">email</div>
                    </div>
                    <div class="broadcast-status-item-detail">
                        <div class="broadcast-status-item-detail-title">{{ $t("message.total") }}</div>
                        <div class="broadcast-status-item-detail-value">{{ broadcastDetail.stats.total }}</div>
                        <div class="broadcast-status-item-detail-description">{{$t("message.totalEmailsProcessed")}}</div>
                    </div>
                </div>
                <div class="broadcast-status-item">
                    <div class="broadcast-status-item-icon" type="delivered">
                        <div class="material-icons">check</div>
                    </div>
                    <div class="broadcast-status-item-detail">
                        <div class="broadcast-status-item-detail-title">
                            {{$t("message.delivered")}} {{ !detailedBroadcastReport ? this.$t('message.mockData') : "" }}
                        </div>
                        <div class="broadcast-status-item-detail-value">{{ broadcastDetail.stats.delivery }}</div>
                        <div class="broadcast-status-item-detail-description">{{$t("message.totalEmailsDelivered")}}</div>
                    </div>
                </div>
                <div class="broadcast-status-item">
                    <div class="broadcast-status-item-icon" type="bounced">
                        <div class="material-icons">error</div>
                    </div>
                    <div class="broadcast-status-item-detail">
                        <div class="broadcast-status-item-detail-title">{{$t("mail.mailSendErrorTitle")}}</div>
                        <div class="broadcast-status-item-detail-value">{{ broadcastDetail.stats.bounce }}</div>
                        <div class="broadcast-status-item-detail-description">{{$t("mail.mailSendErrorDescription")}}</div>
                    </div>
                </div>
                <div class="broadcast-status-item">
                    <div class="broadcast-status-item-icon" type="bounced">
                        <div class="material-icons">error</div>
                    </div>
                    <div class="broadcast-status-item-detail">
                        <div class="broadcast-status-item-detail-title">
                             {{$t("message.complaint")}} {{ !detailedBroadcastReport ? this.$t('message.mockData') : "" }}
                        </div>
                        <div class="broadcast-status-item-detail-value">{{ broadcastDetail.stats.complains }}</div>
                        <div class="broadcast-status-item-detail-description">{{$t("message.totalEmailsComplained")}}</div>
                    </div>
                </div>
                <div class="broadcast-status-item">
                    <div class="broadcast-status-item-icon" type="open">
                        <div class="material-icons">visibility</div>
                    </div>
                    <div class="broadcast-status-item-detail">
                        <div class="broadcast-status-item-detail-title">
                           {{$t("message.opens")}} {{ !detailedBroadcastReport ? this.$t('message.mockData') : "" }}
                        </div>
                        <div class="broadcast-status-item-detail-value">{{ broadcastDetail.stats.open }}</div>
                        <div class="broadcast-status-item-detail-description">{{$t("message.untilNowTotalEmailOpens")}}</div>
                    </div>
                </div>
                <div class="broadcast-status-item">
                    <div class="broadcast-status-item-icon" type="click">
                        <div class="material-icons">link</div>
                    </div>
                    <div class="broadcast-status-item-detail">
                        <div class="broadcast-status-item-detail-title">
                           {{$t("message.clicks")}} {{ !detailedBroadcastReport ? this.$t('message.mockData') : "" }}
                        </div>
                        <div class="broadcast-status-item-detail-value">{{ broadcastDetail.stats.click }}</div>
                        <div class="broadcast-status-item-detail-description">{{$t("message.untilNowTotalEmailLinkClicks")}}</div>
                    </div>
                </div>
                <div class="broadcast-status-item">
                    <div class="broadcast-status-item-icon" type="unique-click">
                        <div class="material-icons">link</div>
                    </div>
                    <div class="broadcast-status-item-detail">
                        <div class="broadcast-status-item-detail-title">
                            {{$t("message.uniqueClicks")}} {{ !detailedBroadcastReport  ? this.$t('message.mockData') : "" }}
                        </div>
                        <div class="broadcast-status-item-detail-value">{{ broadcastDetail.stats.uniqClick }}</div>
                        <div class="broadcast-status-item-detail-description">
                            {{$t("message.untilNowTotalUniqueEmail")}}
                        </div>
                    </div>
                </div>
            </div>
            <div class="broadcast-campaign-chart">
                <div class="broadcast-campaign-chart-title">{{$t("message.emailCampaignTimeline")}}</div>
                <div class="broadcast-campaign-chart-body">
                    <ECharts class="echart-graph" :option="getChartOption()" />
                </div>
            </div>

            <div class="broadcast-campaign-chart">
                <div class="broadcast-campaign-chart-title">{{$t("message.receivedBroadcasts")}}</div>
                <div class="broadcast-campaign-chart-body">
                    <supsis-table
                        :columns="getReceivedBroadcastColumns()"
                        :rows="broadcastDetail.receives"
                        :pagination-cursor="receiverPaginationCursor"
                        @cursor-changed="receiverPaginationChanged"
                        :top-pagination="true"
                        :bottom-pagination="true"
                        :paginationOptions="[5]"
                    />
                </div>
            </div>
        </a-modal>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import SupsisTable from "../../../../components/SupsisTable.vue";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import { MAIL_API } from "../../../../api/mail";
import EmailBroadcast from "./EmailBroadcast.vue";
import { sendSocketMessage, socketMessageStream } from "../../../../utils/socketStream";
import { mapActions, mapState } from "vuex";
import { createComponent, plugin } from "echarts-for-vue";
import * as echarts from "echarts/lib/echarts";
import "echarts/lib/chart/heatmap";
import "echarts/lib/component/tooltip";
import "echarts/lib/component/title";
import "echarts/lib/component/grid";
import "echarts/lib/component/visualMap";
import "echarts/lib/component/legend";
import "echarts/lib/component/toolbox";
import { LineChart } from "echarts/charts";
import _ from "lodash";

echarts.use([LineChart]);

Vue.use(plugin, { echarts });

export default Vue.extend({
    components: {
        SupsisButton,
        SupsisTable,
        EmailBroadcast,
        ECharts: createComponent({ echarts }),
    },
    data() {
        return {
            cancels: [],
            mode: "active",
            loading: false,
            broadcastDetail: null,
            paginationCursor: {
                page: 1,
                perPage: 10,
                total: 0,
            },
            receiverPaginationCursor: {
                page: 1,
                perPage: 5,
                total: 0,
            },
            filters: [],
            isCreatingBroadcast: false,
            showingBroadcast: null,
            monthlyDetail: null,
        };
    },
    computed: {
        ...mapState({
            site: (state) => state.site.site,
            emailBroadcasts: (state) => state.mail.broadcasts,
            detailedBroadcastReport: (state) => _.get(state, "site.features.detailedBroadcastReport", false),
        }),
        columns() {
            return [
                {
                    field: "title",
                    key: "title",
                    title: this.$t("message.title"),
                },
                {
                    field: "description",
                    key: "description",
                    title: this.$t("message.description"),
                },
                {
                    field: "createdAt",
                    key: "createdAt",
                    title: this.$t("ticket.ticketFilterList.creationDate"),
                    renderBodyCell: ({ row }) => {
                        return this.$moment(row.createdAt).format("DD/MM/YYYY HH:mm");
                    },
                },
                {
                    field: "total",
                    key: "total",
                    title: this.$t("message.totalShipments"),
                    renderBodyCell: ({ row }) => {
                        return (
                            <div>
                                {row.total} Mails / {row.lastSentIndex} Sent
                            </div>
                        );
                    },
                },
                {
                    field: "status",
                    key: "status",
                    title: this.$t("message.status"),
                    renderBodyCell: ({ row }) => {
                        const Status = () => {
                            return <div>{this.getStatus(row.status)}</div>;
                        };
                        return row.status === "scheduled" ? (
                            <a-tooltip title={moment(row.scheduleAt).format("DD/MM/YYYY HH:mm")}>
                                <Status />
                            </a-tooltip>
                        ) : (
                            <Status />
                        );
                    },
                },
                {
                    field: "_id",
                    key: "_id",
                    title: this.$t("message.shippingStatus"),
                    renderBodyCell: ({ row }) => {
                        const successPercent = this.getPercent(row.total, row.successMessageCount);
                        const failedPercent = this.getPercent(row.total, row.failedMessageCount);
                        return (
                            <div>
                                <b-progress max={row.lastSentIndex} show-value>
                                    <b-progress-bar value={successPercent} variant="success">
                                        <span>{successPercent} %</span>
                                    </b-progress-bar>
                                    <b-progress-bar value={failedPercent} variant="danger">
                                        <span>{failedPercent} %</span>
                                    </b-progress-bar>
                                </b-progress>
                            </div>
                        );
                    },
                },
                {
                    field: "lastSentIndex",
                    key: "lastSentIndex",
                    title: this.$t("wp.broadcast.progress"),
                    renderBodyCell: ({ row }) => {
                        return (
                            <div>
                                <a-progress percent={this.getPercent(row.total, row.lastSentIndex)} size="small" />
                            </div>
                        );
                    },
                },
                {
                    align: "right",
                    field: "actionsSlot",
                    key: "actionsSlot",
                    title: this.$t("message.transactions"),
                    fixed: "right",
                    renderBodyCell: ({ row }) => {
                        const ArchiveButton = ["completed", "failed", "cancelled"].includes(row.status) &&
                            !row.archived && (
                                <a-tooltip title="Archive">
                                    <a-button
                                        on-click={() => this.archiveBroadcast(row._id)}
                                        class="actionButton d-flex align-items-center"
                                    >
                                        <div class="material-icons">archive</div>
                                    </a-button>
                                </a-tooltip>
                            );

                        const PauseButton = row.status === "running" && (
                            <a-tooltip title="Pause">
                                <a-button
                                    on-click={() => this.sendBroadcastAction(row._id, "pause")}
                                    class="actionButton d-flex align-items-center"
                                >
                                    <div class="material-icons">pause</div>
                                </a-button>
                            </a-tooltip>
                        );

                        const ResumeButton = row.status === "paused" && (
                            <a-tooltip title="Resume">
                                <a-button
                                    on-click={() => this.sendBroadcastAction(row._id, "resume")}
                                    class="actionButton d-flex align-items-center"
                                >
                                    <div class="material-icons">play_arrow</div>
                                </a-button>
                            </a-tooltip>
                        );

                        const CancelButton = ["scheduled", "queued", "running", "paused"].includes(row.status) && (
                            <a-tooltip title="Cancel">
                                <a-button
                                    on-click={() => this.sendBroadcastAction(row._id, "stop")}
                                    class="actionButton d-flex align-items-center"
                                >
                                    <div class="material-icons">cancel</div>
                                </a-button>
                            </a-tooltip>
                        );

                        const DetailsButton = ["completed", "failed", "cancelled"].includes(row.status) && (
                            <a-tooltip title="Details">
                                <a-button
                                    class="actionButton d-flex align-items-center"
                                    on-click={() => this.getBroadcastDetails(row._id)}
                                >
                                    <div class="material-icons">info</div>
                                </a-button>
                            </a-tooltip>
                        );

                        return (
                            <div class="action-buttons">
                                {DetailsButton}
                                {ArchiveButton}
                                {PauseButton}
                                {ResumeButton}
                                {CancelButton}
                            </div>
                        );
                    },
                },
            ];
        },
    },
    methods: {
        ...mapActions({
            setBroadcastList: "mail/setBroadcastList",
        }),
        getPercent(total, lastSentIndex) {
            return Math.round((lastSentIndex / total) * 100);
        },
        setMode(newMode) {
            this.mode = newMode;
            this.paginationCursor.page = 1;
            this.fetch();
        },
        archiveBroadcast(broadcastId) {
            this.$dialog
                .confirm(this.$t("mail.confirmArchive"), {
                    okText: this.$t("mail.archive"), // "Archive" için çeviri
                    cancelText: this.$t("mail.cancel"), // "Cancel" için çeviri
                    customClass: "supsis-dialog",
                })
                .then(() => {
                    this.paginationCursor.page = 1;
                    MAIL_API.BROADCAST.archive(broadcastId)
                        .then(() => {
                            this.$message.success(this.$t("mail.archiveSuccess")); // "Broadcast archived successfully" için çeviri
                            this.fetch();
                        })
                        .catch((error) => {
                            this.$message.error(this.$t("mail.archiveError")); // "Error archiving broadcast" için çeviri
                        });
                });
        },
        sendBroadcastAction(broadcastId, action) {
            const messages = {
                "immediate-run": "email immediate-run-broadcast",
                stop: "email stop-broadcast",
                pause: "email pause-broadcast",
                resume: "email resume-broadcast",
            };
            sendSocketMessage(messages[action], {
                siteId: this.site._id,
                userId: this.$store.state.auth.user._id,
                broadcastId,
            });
        },
        async fetch() {
            this.loading = true;
            const page = this.paginationCursor.page;
            const perPage = this.paginationCursor.perPage;
            try {
                let response = await MAIL_API.BROADCAST.list(page, perPage, this.mode === "archived");
                const { items, total } = response.data;
                console.log("Email broadcasts:", items);
                this.setBroadcastList(items);
                this.paginationCursor.total = total;
            } catch (error) {
                console.error("Error fetching email broadcasts:", error);
            } finally {
                this.loading = false;
            }
        },
        paginationChanged({ page, perPage }) {
            this.paginationCursor.page = page;
            this.paginationCursor.perPage = perPage;
            this.fetch();
        },
        toggleCreateBroadcast() {
            this.isCreatingBroadcast = !this.isCreatingBroadcast;
        },
        onBroadcastCreated(data) {
            this.isCreatingBroadcast = false;
            MAIL_API.BROADCAST.create({
                title: data.title,
                description: data.description,
                template: data.template,
                sender: data.senderEmail,
                templateParams: data.params,
                customerGroup: data.customerGroup,
                scheduleAt: data.scheduleAt,
            }).then((broadcast) => {
               this.$message.success(this.$t("mail.broadcastCreatedSuccess"));
                this.fetch();
                this.sendBroadcastAction(broadcast._id, "immediate-run");
            });
        },
        getBroadcastDetails(broadcastId, updateReceiver = false) {
            MAIL_API.BROADCAST.getEmailBroadcastSentDetails(
                broadcastId,
                this.receiverPaginationCursor.page,
                this.receiverPaginationCursor.perPage
            ).then((response) => {
                if (updateReceiver) {
                    this.broadcastDetail.receives = response.data.receives;
                } else {
                    this.broadcastDetail = response.data;
                }
                this.receiverPaginationCursor.total = this.broadcastDetail.receiverTotal;
                this.showingBroadcast = this.emailBroadcasts.find((broadcast) => broadcast._id === broadcastId);
            });
        },
        getChartXAxis() {
            const chart = this.broadcastDetail.chart;
            const chartLength = chart.length;
            const requiredLength = 10;

            const dates = chart.map((item) => moment(item.date, "YYYY-MM-DD HH:mm"));

            if (chartLength < requiredLength) {
                const fillCount = requiredLength - chartLength;
                const lastDate = dates[chartLength - 1] || moment();
                const filledDates = [];

                for (let i = 1; i <= fillCount; i++) {
                    filledDates.unshift(lastDate.clone().subtract(i, "days"));
                }

                return [...filledDates, ...dates]
                    .sort((a, b) => a.diff(b))
                    .map((date) => date.format("YYYY-MM-DD HH:mm"));
            }
            return dates.map((date) => date.format("YYYY-MM-DD HH:mm"));
        },
        getSeriesData(data) {
            const requiredLength = 10;
            const chartLength = data.length;
            if (chartLength < requiredLength) {
                const fillCount = requiredLength - chartLength;
                return [...new Array(fillCount).fill(0), ...data];
            }
            return data.slice(-10);
        },
        getChartOption() {
            return {
                color: ["#12b981", "#3c82f6", "#f59e0c"],
                tooltip: {
                    trigger: "axis",
                },
                legend: {
                    data: ["Delivered", "Open", "Click"],
                },
                toolbox: {
                    feature: {
                        saveAsImage: {},
                    },
                },
                grid: {
                    left: "3%",
                    right: "4%",
                    bottom: "3%",
                    containLabel: true,
                },
                xAxis: [
                    {
                        type: "category",
                        boundaryGap: false,
                        nameLocation: "middle",
                        data: this.getChartXAxis(),
                    },
                ],
                yAxis: [
                    {
                        type: "value",
                    },
                ],
                series: [
                    {
                        name: "Delivered",
                        type: "line",
                        step: "end",
                        data: this.getSeriesData(this.broadcastDetail.chart.map((item) => item.delivery)),
                    },
                    {
                        name: "Open",
                        type: "line",
                        step: "end",
                        data: this.getSeriesData(this.broadcastDetail.chart.map((item) => item.open)),
                    },
                    {
                        name: "Click",
                        type: "line",
                        step: "end",
                        data: this.getSeriesData(this.broadcastDetail.chart.map((item) => item.click)),
                    },
                ],
            };
        },
        getStatus(status) {
            switch (status) {
                case "completed":
                    return this.$t("message.completed"); // Tamamlandı
                case "failed":
                    return this.$t("message.failed"); // Başarısız
                case "cancelled":
                    return this.$t("message.cancelled"); // İptal edildi
                case "running":
                    return this.$t("message.running"); // Çalışıyor
                case "paused":
                    return this.$t("message.paused"); // Duraklatıldı
                case "queued":
                    return this.$t("message.queued"); // Kuyruğa alındı
                case "scheduled":
                    return this.$t("message.scheduled"); // Planlandı
                default:
                    return status; // Varsayılan durum
            }
        },
        receiverPaginationChanged({ page, perPage }) {
            if (page === this.receiverPaginationCursor.page && perPage === this.receiverPaginationCursor.perPage) {
                return;
            }
            this.receiverPaginationCursor.page = perPage !== this.receiverPaginationCursor.perPage ? 1 : page;
            this.receiverPaginationCursor.perPage = perPage;
            this.getBroadcastDetails(this.showingBroadcast._id, true);
        },
        getReceivedBroadcastColumns() {
            return [
                {
                    field: "lastAction",
                    key: "lastAction",
                    title: this.$t("message.lastAction"),
                    renderBodyCell: ({ row }) => {
                        return moment(row.lastAction).diff(moment(), "days") > 1
                            ? moment(row.lastAction).format("DD/MM/YYYY HH:mm")
                            : moment(row.lastAction).fromNow();
                    },
                },
                {
                    field: "receiver",
                    key: "receiver",
                    title: this.$t("message.receiver"),
                },
                {
                    field: "isDelivered",
                    key: "isDelivered",
                    title: this.$t("message.delivered"),
                    renderBodyCell: ({ row }) => {
                        return row.isDelivered ? (
                            <a-tooltip title={moment(row.deliveryDate).format("DD/MM/YYYY HH:mm")}>
                                <div class="material-icons" style="color: #12b981">
                                    check_circle
                                </div>
                            </a-tooltip>
                        ) : (
                            <div class="material-icons" style="color: #ef4444">
                                cancel
                            </div>
                        );
                    },
                },
                {
                    field: "isOpened",
                    key: "isOpened",
                    title: this.$t("message.opened"),
                    renderBodyCell: ({ row }) => {
                        return row.isOpened ? (
                            <a-tooltip title={moment(row.openedDate).format("DD/MM/YYYY HH:mm")}>
                                <div class="material-icons" style="color: #12b981">
                                    check_circle
                                </div>
                            </a-tooltip>
                        ) : (
                            <div class="material-icons" style="color: #ef4444">
                                cancel
                            </div>
                        );
                    },
                },
                {
                    field: "isClicked",
                    key: "isClicked",
                    title: this.$t("message.clicked"),
                    renderBodyCell: ({ row }) => {
                        return row.isClicked ? (
                            <a-tooltip title={moment(row.lastClickedDate).format("DD/MM/YYYY HH:mm")}>
                                <div class="material-icons" style="color: #12b981">
                                    check_circle
                                </div>{" "}
                                ({row.clickedCount} Times)
                            </a-tooltip>
                        ) : (
                            <div class="material-icons" style="color: #ef4444">
                                cancel
                            </div>
                        );
                    },
                },
                {
                    field: "isBounced",
                    key: "isBounced",
                    title: this.$t("mail.errorStatus"),
                    renderBodyCell: ({ row }) => {
                        return row.isBounced ? (
                            <a-tooltip title={moment(row.bounceDate).format("DD/MM/YYYY HH:mm")}>
                                <div class="material-icons" style="color: #ef4444">
                                    cancel
                                </div>
                            </a-tooltip>
                        ) : (
                            <div class="material-icons" style="color: #ef4444">
                                cancel
                            </div>
                        );
                    },
                },
                {
                    field: "clickedLinks",
                    key: "clickedLinks",
                    title: this.$t("message.clickedLinks"),
                    renderBodyCell: ({ row }) => {
                        return row.clickedLinks.join(", ");
                    },
                },
                {
                    field: "rejectReason",
                    key: "rejectReason",
                    title: this.$t("message.reason"),
                    renderBodyCell: ({ row }) => {
                        return row.rejectReason ? (
                            <a-tooltip title={row.rejectReason}>
                                <div class="material-icons" style="color: #ef4444">
                                    info
                                </div>
                            </a-tooltip>
                        ) : (
                            "-"
                        );
                    },
                },
            ];
        },
        closeBroadcastDetail() {
            this.broadcastDetail = null;
            this.showingBroadcast = null;
            this.receiverPaginationCursor = {
                page: 1,
                perPage: 10,
                total: 0,
            };
        },
        updateMailBroadcast(data) {
            const {
                broadcastId,
                status,
                total,
                lastSentIndex,
                successMessageCount,
                failedMessageCount,
                reason,
                finishedAt,
            } = data;
            this.setBroadcastList(
                this.emailBroadcasts.map((broadcast) => {
                    if (broadcast._id === broadcastId) {
                        broadcast.successMessageCount = successMessageCount || broadcast.successMessageCount;
                        broadcast.failedMessageCount = failedMessageCount || broadcast.failedMessageCount;
                        broadcast.status = status || broadcast.status;
                        broadcast.total = total || broadcast.total;
                        broadcast.lastSentIndex = lastSentIndex || broadcast.lastSentIndex;
                        broadcast.reason = reason || broadcast.reason;
                        broadcast.finishedAt = finishedAt || broadcast.finishedAt;
                    }
                    return broadcast;
                })
            );
        },
    },
    beforeDestroy() {
        this.cancels.forEach((cancel) => cancel());
    },
    mounted() {
        MAIL_API.BROADCAST.getEmailBroadcastMonthDetail().then((response) => {
            this.monthlyDetail = response.data;
        });
        const listener = socketMessageStream.subscribe(({ cmd, data }) => {
            console.log("BroadcastBroadcastList event", cmd, data);
            if (cmd === "broadcast_status") {
                this.updateMailBroadcast(data);
            }
        });
        this.cancels.push(() => {
            listener.unsubscribe();
        });
        this.fetch();
    },
});
</script>

<style lang="scss">
.supsis-dialog {
    .dg-content-footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 5px;
        .dg-btn--cancel {
            color: #22272d;
            background-color: #fefefe;
            border-color: #22272d;
        }
        .dg-btn--ok {
            color: #fff;
            background-color: #22272d;
            border-color: #22272d;
        }
    }
}
</style>
<style lang="scss">
.email-broadcast-list {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 20px;

    .header-container {
        h2 {
            margin: 0;
            font-size: 24px;
            font-weight: 600;
        }
    }

    .toolbar-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px;
        border-radius: 4px;
        margin-bottom: 10px;
        &:has(.right-section) {
            background-color: #f5f5f5;
        }

        .left-section {
            display: flex;
            align-items: center;
            gap: 24px;

            .view-switcher {
                .supsis-button-group {
                    display: flex;
                    gap: 8px;
                }
            }
        }
    }

    .action-buttons {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 3px;

        .actionButton {
            display: flex;
            align-items: center;
            text-wrap: none;
            gap: 4px;
        }
    }
}

.broadcast-detail {
    margin-top: 15px;
    display: grid;
    grid-template-columns: calc(25% - 4px) calc(25% - 4px) calc(25% - 4px) calc(25% - 4px);
    column-gap: 10px;
    flex-wrap: wrap;

    &-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        background-color: #6266f1;
        padding: 10px;
        border-radius: 5px;
        color: white;

        &.success {
            background-color: #12b981;
        }

        &.failed {
            background-color: #ef4444;
        }

        &[status="completed"] {
            background-color: #12b981;
        }

        &[status="failed"] {
            background-color: #ef4444;
        }

        &[status="cancelled"] {
            background-color: #f59e0c;
        }

        &[status="running"] {
            background-color: #3c82f6;
        }

        &[status="paused"] {
            background-color: #f59e0c;
        }

        &[status="queued"] {
            background-color: #f59e0c;
        }

        &[status="scheduled"] {
            background-color: #f59e0c;
        }

        &[completed="true"] {
            background-color: #12b981;
        }

        &-title {
            font-size: 16px;
            font-weight: 500;
        }

        &-value {
            font-size: 18px;
            font-weight: 600;
        }
    }
}

.broadcast-status {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    margin-top: 15px;

    &-item {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 274px;

        &-icon {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 60px;
            height: 60px;
            min-width: 60px;
            height: 60px;
            border-radius: 5px;

            &[type="total"] {
                background-color: #6266f1;
            }

            &[type="delivered"] {
                background-color: #12b981;
            }

            &[type="bounced"] {
                background-color: #ef4444;
            }

            &[type="open"] {
                background-color: #3c82f6;
            }

            &[type="click"] {
                background-color: #f59e0c;
            }

            &[type="unique-click"] {
                background-color: #0c9cf5;
            }

            .material-icons {
                font-size: 32px;
                color: white;
            }
        }

        &-detail {
            display: flex;
            flex-direction: column;

            &-title {
                font-size: 16px;
                font-weight: 500;
            }

            &-value {
                font-size: 18px;
                font-weight: 600;
            }

            &-description {
                font-size: 12px;
                color: #6b7280;
            }
        }
    }
}

.broadcast-campaign-chart {
    margin-top: 20px;
    .broadcast-campaign-chart-title {
        font-size: 20px;
        font-weight: 600;
        margin-bottom: 16px;
    }

    .broadcast-campaign-chart-body {
        height: 500px;
    }
}
</style>
